import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/2zc3j2vv_jini%20studio%20logo%20.png";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-neutral-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Jini Studio" className="h-16 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl font-bold text-neutral-900 mb-8">Terms and Conditions</h1>
        <p className="text-sm text-neutral-500 mb-8">Last updated: February 1, 2026</p>

        <div className="prose prose-neutral max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              By accessing or using the Jini Studio AI platform ("Service"), you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">2. Description of Service</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Jini Studio AI is an AI-powered photoshoot platform that allows users to upload product images and generate professional AI-enhanced photoshoot images and videos. The Service is provided by Zaroori Retail.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">3. User Accounts</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              You must create an account to use our Service. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Not share your account credentials with third parties</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">4. Intellectual Property Rights</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>Your Content:</strong> You retain all rights to the original product images you upload. By uploading content, you grant us a license to process and transform these images using our AI technology.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>Generated Content:</strong> AI-generated images and videos created through our Service are licensed to you according to your subscription plan. Commercial use rights are included in Pro and Studio plans.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>Our Content:</strong> The Service, including its original content, features, and functionality, is owned by Zaroori Retail and is protected by international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">5. Acceptable Use</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">You agree not to use the Service to:</p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li>Upload content that infringes on any third party's intellectual property rights</li>
              <li>Generate content that is illegal, harmful, threatening, or discriminatory</li>
              <li>Attempt to circumvent any technological measures implemented by the Service</li>
              <li>Use the Service for any purpose that is unlawful or prohibited by these Terms</li>
              <li>Resell or redistribute generated content without appropriate licensing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">6. Subscription and Payments</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Our Service operates on a subscription basis. Subscription fees are charged monthly in advance. You may cancel your subscription at any time, but no refunds will be provided for partial months.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Usage limits are determined by your subscription plan. Exceeding these limits may result in service interruption or additional charges. Please refer to our Pricing page for current plan details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">7. AI-Generated Content Disclaimer</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 font-medium mb-2">Important Notice About AI-Generated Content</p>
              <p className="text-amber-700 text-sm">
                All images and videos generated through our Service are created using artificial intelligence technology. 
                The AI models depicted are computer-generated and do not represent real individuals.
              </p>
            </div>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>AI Model Rights:</strong> The virtual models used in our Service are AI-generated representations and are not based on any real person. No model release or consent from real individuals is required for these generated images.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>Commercial Usage:</strong> Generated content is licensed for commercial use including e-commerce listings, marketing materials, social media, and advertising campaigns, subject to your subscription plan terms.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>Quality Disclaimer:</strong> While we strive for high-quality output, AI generation may occasionally produce imperfect results. We offer re-generation options for unsatisfactory outputs within your plan limits.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>No Deepfake Policy:</strong> You agree not to use our Service to create content that impersonates real individuals, generates misleading content, or creates non-consensual imagery.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              The Service is provided "as is" without warranties of any kind. Zaroori Retail shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Our total liability for any claims arising from these Terms or the Service shall not exceed the amount paid by you in the twelve months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">9. Changes to Terms</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes via email or through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">10. Governing Law</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of Mumbai, Maharashtra.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">11. Contact Information</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-neutral-50 rounded-lg p-4 text-neutral-600">
              <p className="font-medium">Zaroori Retail (Jini Studio AI)</p>
              <p>Email: legal@jinistudio.ai</p>
              <p>Address: Mumbai, Maharashtra, India</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-100 py-8 px-6 mt-12">
        <div className="max-w-4xl mx-auto text-center text-neutral-600 text-sm">
          <p>© 2025 Jini Studio by Zaroori Retail. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/privacy" className="hover:text-indigo-600">Privacy Policy</Link>
            <span>•</span>
            <Link to="/refund" className="hover:text-indigo-600">Refund Policy</Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-indigo-600">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
